import React, { Suspense, lazy, useState, useEffect, useRef } from "react";
import { FiMenu } from "react-icons/fi";
import "./Nav.css";
import logo from "../../assets/logo.png";
import logowhite from "../../assets/whitelogo.png";
import { Link } from "react-router-dom";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "../../context/ThemeProvider";
import useAuth from "../../hooks/useAuth";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { IoPerson } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import { MdMenu } from "react-icons/md";
import { HiOutlineHeart, HiOutlineShoppingCart, HiOutlineMoon } from "react-icons/hi";
import { useCart } from "../../context/CartContext";
import { hasActiveSubscription } from "../../utils/subscriptionUtils";
import SearchInput from "../SearchPage/SearchInput";
import AuthVerifyingOverlay from "../AuthVerifyingOverlay";
import { isCertifiedCreator as hasCertifiedCreatorAccess } from "../Creator/creatorAccess";
import { StaffEmbeddingAlertBell } from "./StaffEmbeddingAlert";

const SignIn = lazy(() => import("../SignIn/SignIn"));
const SignUp = lazy(() => import("../SignUp/SignUp"));

const Nav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  // console.log(auth);//
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const { darkMode, toggleTheme } = useTheme();
  const [showProfileDropDown, setShowProfileDropDown] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { cartCount } = useCart();
  const isSubscribed = hasActiveSubscription(auth?.subscription);

  const logout = async () => {
    console.log("clicked logout");
    try {
      setIsLoggingOut(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/logout`,
      );
      if (response.data) {
        window.location.reload();
        navigate("/");
      }
    } catch (error) {
      console.error("Error changing password:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const profileDropdownRef = useRef(null);
  const profileButtonRef = useRef(null);
  const mobileNavRef = useRef(null);
  const mobileToggleRef = useRef(null);
  const [showHeaderSearch, setShowHeaderSearch] = useState(false);

  const pathname = location.pathname || "/";
  const isAdminLikeRoute =
    pathname.startsWith("/admin") || pathname.startsWith("/dashboard");
  const hasBodySearch = pathname === "/" || pathname === "/search";
  const scrollThreshold = pathname === "/" ? 520 : 260;
  const customerProfileSections = [
    {
      label: "Account",
      items: [
        { label: "Account", to: "/dashboard?account=1" },
        { label: "Purchased Videos", to: "/dashboard?tab=purchased" },
        { label: "Download History", to: "/dashboard?tab=downloads" },
        { label: "Wishlist", to: "/dashboard?tab=saved" },
        { label: "Orders", to: "/dashboard?tab=orders" },
        { label: "Payments", to: "/dashboard?tab=payments" },
        { label: "Subscriptions", to: "/dashboard?tab=subscriptions" },
      ],
    },
    {
      label: "Support & policies",
      items: [
        { label: "FAQ", to: "/faqs" },
        { label: "Privacy Policy", to: "/privacypolicy" },
        { label: "Terms & Conditions", to: "/termsandconditions" },
        { label: "About Us", to: "/about" },
      ],
    },
  ];

  const closeProfileMenu = ({ closeMobile = false } = {}) => {
    setShowProfileDropDown(false);
    if (closeMobile) {
      setMenuOpen(false);
    }
  };

  const isCertifiedCreator = hasCertifiedCreatorAccess(auth);

  const renderProfileMenuItems = ({ mobile = false } = {}) => {
    const closeMenu = () => closeProfileMenu({ closeMobile: mobile });

    if (auth.role === "ADMIN" || auth.role === "EMPLOYEE") {
      return (
        <>
          <Link to="/dashboard" onClick={closeMenu} className="profileMenuLink">
            Dashboard
          </Link>
          <button
            type="button"
            onClick={() => {
              closeMenu();
              logout();
            }}
            className="profileMenuLogout"
          >
            Logout
          </button>
        </>
      );
    }

    return (
      <>
        {isCertifiedCreator ? (
          <div className="profileMenuSection">
            <div className="profileMenuHeading">Creator Mode</div>
            {location.pathname.startsWith("/creator/dashboard") ? (
              <Link
                to="/"
                onClick={closeMenu}
                className="profileMenuLink font-semibold text-[var(--primary)]"
              >
                Switch to User
              </Link>
            ) : (
              <Link
                to="/creator/dashboard"
                onClick={closeMenu}
                className="profileMenuLink font-semibold text-[var(--primary)]"
              >
                Switch to Creator
              </Link>
            )}
          </div>
        ) : (
          <div className="profileMenuSection">
            <Link
              to="/creator"
              onClick={closeMenu}
              className="profileMenuLink font-semibold text-[var(--primary)]"
            >
              Become a Creator
            </Link>
          </div>
        )
        }

        {customerProfileSections.map((section) => (
          <div key={section.label} className="profileMenuSection">
            <div className="profileMenuHeading">{section.label}</div>
            {section.items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={closeMenu}
                className="profileMenuLink"
              >
                {item.label}
              </Link>
            ))}
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            closeMenu();
            logout();
          }}
          className="profileMenuLogout"
        >
          Logout
        </button>
      </>
    );
  };

  useEffect(() => {
    setMenuOpen(false);
    setShowProfileDropDown(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(event.target)
      ) {
        setShowProfileDropDown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileNavRef.current &&
        !mobileNavRef.current.contains(event.target) &&
        mobileToggleRef.current &&
        !mobileToggleRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isAdminLikeRoute) {
      setShowHeaderSearch(false);
      return undefined;
    }

    if (!hasBodySearch) {
      setShowHeaderSearch(true);
      return undefined;
    }

    const updateHeaderSearchVisibility = () => {
      setShowHeaderSearch(window.scrollY >= scrollThreshold);
    };

    updateHeaderSearchVisibility();
    window.addEventListener("scroll", updateHeaderSearchVisibility, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", updateHeaderSearchVisibility);
    };
  }, [hasBodySearch, isAdminLikeRoute, scrollThreshold]);

  return (
    <>
      {isLoggingOut && (
        <AuthVerifyingOverlay
          message="Logging you out"
          darkMode={darkMode}
        />
      )}
      <header
        className={`NavContainer${darkMode ? " dark" : ""}${menuOpen ? " menu-open" : ""
          }`}
        role="banner"
      >
        <nav className="Nav w-full" aria-label="Main navigation">
          <button
            type="button"
            onClick={() => {
              setMenuOpen((prev) => !prev);
              setShowProfileDropDown(false);
            }}
            className="navToggle ms-1 mt-1.5 text-[28px]"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            ref={mobileToggleRef}
          >
            {menuOpen ? <RxCross2 /> : <MdMenu />}
          </button>
          {auth.role === "ADMIN" ? (
            <a className="navLogo mt-1.5" href="/?page=1">
              <img
                src={darkMode ? logowhite : logo}
                alt="Shotkut logo"
                width="160"
                height="36"
                className="object-contain h-full"
              />
            </a>
          ) : (
            <a className="navLogo mt-1.5
            5" href="/?page=1">
              <img
                src={darkMode ? logowhite : logo}
                alt="Shotkut logo"
                width="160"
                height="36"
                className="object-contain w-full h-full"
                style={{ objectPosition: "left center" }}
              />
            </a>
          )}

          {showHeaderSearch && (
            <div className="hidden md:flex headerSearchSlot me-9 mt-1 ">
              <SearchInput variant="header" />
            </div>
          )}

          <div className="hidden md:flex gap-6 items-center ml-auto navActions">
            {/* <Link to="/">
              <div className={`navBtn hover:text-[var(--primary)] transition-all duration-250 ${location.pathname === '/' ? 'text-[var(--primary)] border-b-[3px] border-[var(--primary)] rounded-b-none' : ''}`}>
                Home
              </div>
            </Link> */}
            {!isSubscribed && (
              <Link to="/pricing">
                <div className={`navBtn hover:text-[var(--primary)] transition-all duration-250 ${location.pathname === '/pricing' ? 'text-[var(--primary)] border-b-[3px] border-[var(--primary)] rounded-b-none' : ''}`}>
                  Pricing
                </div>
              </Link>
            )}

            {auth.role && (
              <Link to="/dashboard">
                <div className={`navBtn hover:text-[var(--primary)] transition-all duration-250 ${location.pathname.startsWith('/dashboard') ? 'text-[var(--primary)] border-b-[3px] border-[var(--primary)] rounded-b-none' : ''}`}>
                  Dashboard
                </div>
              </Link>
            )}

            {isCertifiedCreator ? (
              location.pathname.startsWith("/creator/dashboard") ? (
                <Link to="/">
                  <div className="navBtn hover:text-[var(--primary)] transition-all duration-250 font-medium">
                    Switch to User
                  </div>
                </Link>
              ) : (
                <Link to="/creator/dashboard">
                  <div className="navBtn hover:text-[var(--primary)] transition-all duration-250 font-medium text-[var(--primary)]">
                    Switch to Creator
                  </div>
                </Link>
              )
            ) : (
              // <Link to="/creator">
              //   <div className="navBtn hover:text-[var(--primary)] transition-all duration-250 font-medium">
              //     Become a Creator
              //   </div>
              // </Link>
              <></>
            )}

            <div style={{ width: "2px", height: "20px", background: "grey" }} />

            {auth.role === "USER" && (
              <Link
                to="/dashboard?tab=saved"
                aria-label="Wishlist"
                title="Wishlist"
                className="cursor-pointer text-[var(--primary)] hover:text-[var(--hover)] transition-all duration-250"
              >
                <HiOutlineHeart className="text-2xl" />
              </Link>
            )}

            {!isSubscribed && (
              <Link
                to="/cart"
                aria-label="Cart"
                className="relative cursor-pointer text-[var(--primary)] hover:text-[var(--hover)] transition-all duration-250"
              >
                <HiOutlineShoppingCart className="text-2xl" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[var(--primary)] text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCount > 9 ? "9+" : cartCount}
                  </span>
                )}
              </Link>
            )}

            {(auth.role === "ADMIN" || auth.role === "EMPLOYEE") && (
              <StaffEmbeddingAlertBell />
            )}

            <HiOutlineMoon
              className="text-2xl cursor-pointer transition-all duration-250 text-[var(--primary)]"
              onClick={toggleTheme}
              title="Toggle Theme"
            />
            {/* <ThemeToggle /> */}
            <div style={{ width: "2px", height: "20px", background: "grey" }} />
          </div>

          {/* Right Header Controls (Profile / Sign In + Mobile Hamburger) */}
          <div className="flex items-center gap-3 ml-auto md:ml-0">
            {(auth.role === "ADMIN" || auth.role === "EMPLOYEE") && (
              <div className="md:hidden">
                <StaffEmbeddingAlertBell />
              </div>
            )}
            {auth.role ? (
              <div
                ref={profileButtonRef}
                onClick={() => {
                  setShowProfileDropDown((prev) => !prev);
                  setMenuOpen(false);
                }}
                className="cursor-pointer hover:opacity-80 transition-all duration-250 flex items-center"
              >
                {auth.profilePicture ? (
                  <img
                    src={auth.profilePicture}
                    alt="Profile"
                    className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover border-2 border-[var(--primary)]"
                  />
                ) : (
                  <IoPerson className="text-2xl md:text-3xl" />
                )}
              </div>
            ) : (
              <div
                className={`green-button rounded-full ${darkMode ? "text-black" : "text-black"}`}
                onClick={() => setShowSignInModal(true)}
              >
                Sign In
              </div>
            )}
          </div>

          {/* Mobile Menu */}
          <div
            ref={mobileNavRef}
            className={`absolute top-[58px] left-0 w-full flex flex-col px-4 gap-2 shadow-md transition-all duration-300 ease-in-out rounded border
                ${darkMode
                ? "bg-[#1B1D1C] border-[#333333]"
                : "bg-[#F1F1F1] border-[#CBCBCB]"
              }
                ${menuOpen
                ? "opacity-100 h-auto max-h-[calc(100vh-80px)] pt-6 pb-8 overflow-y-auto"
                : "opacity-0 py-0 max-h-0 overflow-y-hidden pointer-events-none"
              }`}
          >
            <Link to="/" onClick={() => setMenuOpen(false)}>
              <div className={`navBtn hover:text-[var(--primary)] transition-all duration-250 ${location.pathname === '/' ? 'text-[var(--primary)] border-l-[3px] border-[var(--primary)] rounded-l-none' : ''}`}>
                Home
              </div>
            </Link>
            {!isSubscribed && (
              <Link to="/pricing" onClick={() => setMenuOpen(false)}>
                <div className={`navBtn hover:text-[var(--primary)] transition-all duration-250 ${location.pathname === '/pricing' ? 'text-[var(--primary)] border-l-[3px] border-[var(--primary)] rounded-l-none' : ''}`}>
                  Pricing
                </div>
              </Link>
            )}


            {auth.role && (
              <Link to="/dashboard" onClick={() => setMenuOpen(false)}>
                <div className={`navBtn hover:text-[var(--primary)] transition-all duration-250 ${location.pathname.startsWith('/dashboard') ? 'text-[var(--primary)] border-l-[3px] border-[var(--primary)] rounded-l-none' : ''}`}>
                  Dashboard
                </div>
              </Link>
            )}

            {isCertifiedCreator ? (
              location.pathname.startsWith("/creator/dashboard") ? (
                <Link to="/" onClick={() => setMenuOpen(false)}>
                  <div className="navBtn hover:text-[var(--primary)] transition-all duration-250 font-medium">
                    Switch to User
                  </div>
                </Link>
              ) : (
                <Link to="/creator/dashboard" onClick={() => setMenuOpen(false)}>
                  <div className="navBtn hover:text-[var(--primary)] transition-all duration-250 font-medium text-[var(--primary)]">
                    Switch to Creator
                  </div>
                </Link>
              )
            ) : (
              <Link
                to="/creator"
                onClick={() => setMenuOpen(false)}
                className="navBtn hover:text-[var(--primary)] transition-all duration-250 font-medium"
              >
                Become a Creator
              </Link>
            )}

            {auth.role === "USER" && (
              <Link
                to="/dashboard?tab=saved"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 text-[var(--primary)] hover:text-[var(--hover)] transition-all duration-250"
              >
                <div className="navBtn hover:text-[var(--primary)] transition-all duration-250 flex items-center gap-2">
                  <HiOutlineHeart className="text-xl" />
                  Wishlist
                </div>
              </Link>
            )}

            {!isSubscribed && (
              <Link
                to="/cart"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 text-[var(--primary)] hover:text-[var(--hover)] transition-all duration-250"
              >
                <div className="navBtn hover:text-[var(--primary)] transition-all duration-250 flex items-center gap-2">
                  <HiOutlineShoppingCart className="text-xl" />
                  Cart
                  {cartCount > 0 && (
                    <span className="bg-[var(--primary)] text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {cartCount > 9 ? "9+" : cartCount}
                    </span>
                  )}
                </div>
              </Link>
            )}

            <ThemeToggle />
          </div>

          {/* Profile Dropdown */}
          <div
            ref={profileDropdownRef}
            className={`absolute top-[58px] right-3 sm:right-6 md:right-4 w-[260px] sm:w-[280px] px-2 flex flex-col gap-1 shadow-md transition-all duration-300 ease-in-out rounded border z-50
                ${darkMode
                ? "bg-[#1B1D1C] border-[#333333]"
                : "bg-[#F1F1F1] border-[#CBCBCB]"
              }
                ${showProfileDropDown
                ? "opacity-100 h-auto max-h-[calc(100vh-80px)] py-2 overflow-y-auto pointer-events-auto"
                : "opacity-0 py-0 max-h-0 overflow-y-hidden pointer-events-none"
              }`}
          >
            {renderProfileMenuItems()}
          </div>
        </nav>
      </header>

      <Suspense fallback={null}>
        {showSignInModal && (
          <SignIn
            setShowModal={setShowSignInModal}
            switchToSignUp={() => {
              setShowSignInModal(false);
              setShowSignUpModal(true);
            }}
          />
        )}
        {showSignUpModal && (
          <SignUp
            setShowModal={setShowSignUpModal}
            switchToSignIn={() => {
              setShowSignUpModal(false);
              setShowSignInModal(true);
            }}
          />
        )}
      </Suspense>
    </>
  );
};

export default Nav;
